import { VehiclePhotosStrip } from "./RentalFleetExtras";
import { useEffect, useRef, useState } from "react";
import { VehicleInspectionForm } from "../VehicleInspectionForm";
import { isMotoText } from "../VehicleDamageDiagram";
import { toast } from "sonner";
import { Loader2, Camera, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api, fileUrl } from "../../lib/api";

const FUEL = [0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];
export const fuelLabel = (v) => `${Math.round((Number(v) || 0) / 12.5)}/8`;
const today = () => new Date().toISOString().slice(0, 10);

const PhotoPicker = ({ photos, setPhotos, testid }) => {
  const [up, setUp] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUp(true);
    try { for (const f of files) { const r = await api.uploadFile(f); setPhotos((p) => [...p, { id: r.attachment_id, name: r.attachment_name }]); } }
    catch { toast.error("تعذّر رفع الصورة"); } finally { setUp(false); }
  };
  return (
    <div className="space-y-2">
      <label data-testid={testid} className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50">
        {up ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} صور المركبة ({photos.length})
        <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pick} />
      </label>
      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {photos.map((p, i) => (
            <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
              <img src={fileUrl(p.id)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))} className="absolute top-0.5 left-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Line = ({ l, v, cls = "" }) => (<div className={`flex justify-between text-sm ${cls}`}><span>{l}</span><span className="tabular-nums">{v}</span></div>);

// mode: "out" (handover to renter) | "in" (vehicle return with auto charges)
export const RentalHandoverDialog = ({ contract, mode, onClose, onDone }) => {
  const isIn = mode === "in";
  const [f, setF] = useState({});
  const [photos, setPhotos] = useState([]);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const inspRef = useRef(null);
  const kind = contract?.vehicle_kind === "moto" || isMotoText(contract?.vehicle_type) ? "moto" : "car";
  useEffect(() => {
    if (!contract) return;
    setF({ odometer: "", fuel_level: isIn ? (contract.handover_out?.fuel_level ?? 100) : 100, return_date: today(), return_time: new Date().toTimeString().slice(0, 5), damage_amount: "", damage_notes: "", violations_amount: "", notes: "" });
    setPhotos([]); setPrev(null);
  }, [contract, isIn]);
  const body = { ...f, odometer: Number(f.odometer) || 0, fuel_level: Number(f.fuel_level), damage_amount: Number(f.damage_amount) || 0, violations_amount: Number(f.violations_amount) || 0, photos };
  useEffect(() => {
    if (!contract || !isIn || !f.return_date) return;
    const h = setTimeout(() => api.rentalReturnPreview(contract.id, body).then(setPrev).catch(() => setPrev(null)), 300);
    return () => clearTimeout(h);
  }, [contract, isIn, f.return_date, f.return_time, f.odometer, f.fuel_level, f.damage_amount, f.violations_amount]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.odometer) { toast.warning("أدخل قراءة العداد"); return; }
    setBusy(true);
    try {
      const inspection = inspRef.current?.getInspection() || null;
      const r = isIn ? await api.rentalReturn(contract.id, { ...body, inspection }) : await api.rentalHandover(contract.id, { ...body, inspection });
      toast.success(isIn ? "تم استلام المركبة وإغلاق العقد" : "تم تسجيل تسليم المركبة"); onDone(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const ch = prev?.charges;
  const out = contract?.handover_out;
  return (
    <Dialog open={!!contract} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg font-body max-h-[92vh] overflow-y-auto" dir="rtl" data-testid={`rental-${isIn ? "return" : "handover"}-dialog`}>
        <DialogHeader><DialogTitle>{isIn ? "استلام المركبة (إرجاع)" : "تسليم المركبة للمستأجر"} — {contract?.number}</DialogTitle>
          <DialogDescription>{contract?.vehicle_identifier} · {contract?.renter_name}{isIn && contract?.end_time ? ` · موعد الإرجاع ${contract.end_date} ${contract.end_time}` : ""}{isIn && out ? ` · عند التسليم: عداد ${out.odometer} — وقود ${fuelLabel(out.fuel_level)}` : ""}</DialogDescription></DialogHeader>
        {(contract?.vehicle_photos || []).length > 0 && <div><p className="mb-1 text-xs font-semibold text-slate-500">صور المركبة المرجعية — قارن الحالة</p><VehiclePhotosStrip photos={contract.vehicle_photos} testid="rental-handover-ref-photos" /></div>}
        {!isIn && contract && !contract.authorized && (
          <div data-testid="rental-handover-auth-warning" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">تنبيه: لم يتم تفويض المستأجر بعد (أبشر / نقل). تأكد من التفويض قبل تسليم المركبة.</div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {isIn && <div className="space-y-1.5"><Label>تاريخ الإرجاع</Label><Input data-testid="rental-return-date" type="date" dir="ltr" value={f.return_date || ""} onChange={set("return_date")} /></div>}
          {isIn && <div className="space-y-1.5"><Label>وقت الإرجاع</Label><input data-testid="rental-return-time" type="time" dir="ltr" value={f.return_time || ""} onChange={set("return_time")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" /></div>}
          <div className="space-y-1.5"><Label>قراءة العداد (كم)</Label><Input data-testid="rental-odometer" type="number" dir="ltr" value={f.odometer ?? ""} onChange={set("odometer")} /></div>
          <div className="space-y-1.5"><Label>مستوى الوقود</Label>
            <select data-testid="rental-fuel-level" value={f.fuel_level ?? 100} onChange={set("fuel_level")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
              {FUEL.map((v) => <option key={v} value={v}>{fuelLabel(v)}{v === 100 ? " (ممتلئ)" : v === 0 ? " (فارغ)" : ""}</option>)}</select></div>
          {isIn && <>
            <div className="space-y-1.5"><Label>قيمة الأضرار</Label><Input data-testid="rental-damage-amount" type="number" dir="ltr" value={f.damage_amount} onChange={set("damage_amount")} /></div>
            <div className="space-y-1.5"><Label>مخالفات مرورية</Label><Input data-testid="rental-violations-amount" type="number" dir="ltr" value={f.violations_amount} onChange={set("violations_amount")} /></div>
            <div className="col-span-2"><Input data-testid="rental-damage-notes" placeholder="وصف الأضرار" value={f.damage_notes} onChange={set("damage_notes")} /></div>
          </>}
        </div>
        <PhotoPicker photos={photos} setPhotos={setPhotos} testid={`rental-${isIn ? "return" : "handover"}-photos`} />
        {contract && <VehicleInspectionForm key={`${contract.id}-${mode}`} ref={inspRef} stage={isIn ? "الاستلام" : "التسليم"} kind={kind} noPhotos noOdometer />}
        <Input data-testid="rental-handover-notes" placeholder="ملاحظات" value={f.notes || ""} onChange={set("notes")} />
        {isIn && ch && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1 text-slate-600" data-testid="rental-return-preview">
            <Line l={`تأخير ${ch.late_days} يوم × ${ch.daily_rate}`} v={ch.late} />
            <Line l={`كيلومترات زائدة ${ch.extra_km} كم (المسموح ${ch.km_allowed || "مفتوح"})`} v={ch.extra_km_amount} />
            <Line l={`وقود ناقص ${ch.fuel_short}%`} v={ch.fuel} />
            <Line l="أضرار" v={ch.damage} />
            {ch.vat > 0 && <Line l="ضريبة 15% على الإضافات" v={ch.vat} />}
            <Line l="مخالفات مرورية" v={ch.violations} />
            <Line l="إجمالي الإضافات" v={ch.total} cls="font-bold text-slate-900 border-t border-slate-200 pt-1" />
            <Line l="إجمالي العقد بعد الإضافات" v={prev.grand_total} />
            <Line l="المدفوع" v={prev.paid} />
            <Line l="التأمين المحتجز" v={prev.deposit_held} />
            <Line l={prev.balance > 0 ? "المتبقي على المستأجر" : "رصيد للمستأجر"} v={Math.abs(prev.balance)} cls="font-bold text-rose-600" />
          </div>
        )}
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid={`rental-${isIn ? "return" : "handover"}-save`} disabled={busy} onClick={save} className={`${isIn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"} gap-1.5`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isIn ? "استلام وإغلاق العقد" : "تأكيد التسليم"}</Button>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
